import axios from 'axios';
import getBaseUserUrl from './baseUserUrl';
import { configureHttpHeaders, filterRequestPayloadObject } from './apiHelpers';

const baseUrl = getBaseUserUrl();
let controller;

export async function getUserCount(firstName = '', lastName = '', email = '') {
  configureHttpHeaders(axios);
  try {
    const res = await axios.get(
      `${baseUrl}users/count?searchFirst=${firstName}&searchLast=${lastName}&searchEmail=${email}`,
    );
    return onSuccess(res);
  } catch (err) {
    return onError(err);
  }
}

export function getUsers(
  firstName = '',
  lastName = '',
  email = '',
  mid = '',
  pageNumber = 1,
  pageSize = 25,
  sortField = '',
  sortAscending = true,
) {
  configureHttpHeaders(axios);
  const midQs = mid === '' ? '' : `&mid=${mid.replace(/(^[0 ]+)|(^\s+|\s+$)/g, '')}`;
  return Promise.all([
    axios.get(
      `${baseUrl}users/paged?searchFirst=${firstName}&searchLast=${lastName}&searchEmail=${email}${midQs}&page=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&sortAscending=${sortAscending}`,
    ),
    axios.get(`${baseUrl}users/count?searchFirst=${firstName}&searchLast=${lastName}&searchEmail=${email}${midQs}`),
  ])
    .then(([data, dataCount]) => {
      return { data, dataCount };
    })
    .catch(onError);
}

export function getFullUserById(userId) {
  configureHttpHeaders(axios);

  return Promise.all([axios.get(`${baseUrl}users/${userId}`), getUserOrganizationsTopLevel(userId)])
    .then(([userResponse, dataAccess]) => {
      const response = {
        ...userResponse.data,
        dataAccess,
      };

      // This is part of a front end fix (DE55323) to prevent users from granting more access than they have
      // when saving a dataAccess row. When a user selects 'All' for a hierarchy tier this is now expanded to the
      // specific orgs at that tier that the user has access to when saving the user.
      // Here we are loading the record back for editing, and need to remove the multiple leaf orgs to set it back to 'All'
      // eslint-disable-next-line no-unused-vars
      const collapseMultiplesToAll = (orgs) => {
        orgs.forEach((o) => {
          if (o.organizations.length > 0 && o.orgLevelIndex < 4) {
            const lastLevel = !o.organizations.some((x) => x.organizations.length > 0);
            if (lastLevel) {
              o.hasAccess = true;
              o.organizations = [];
            } else {
              collapseMultiplesToAll(o.organizations);
            }
          }
        });
      };

      // collapseMultiplesToAll(dataAccess);
      response.dataAccess = dataAccess;
      return response;
    })
    .catch(onError);
}

export function getUserById(userId) {
  configureHttpHeaders(axios);
  if (controller !== undefined) {
    controller.abort();
  }
  // regenerate AbortController
  controller = new AbortController();
  return get(`users/${userId}`, controller.signal);
}

export function getViewUserById(userId) {
  configureHttpHeaders(axios);
  if (controller !== undefined) {
    controller.abort();
  }
  // regenerate AbortController
  controller = new AbortController();
  return Promise.all([
    axios.get(`${baseUrl}users/${userId}`, {
      signal: controller.signal,
    }),
    axios.get(`${baseUrl}users/${userId}/merchants`, {
      signal: controller.signal,
    }),
  ])
    .then(([userResponse, merchants]) => {
      const processedMerchants = processMerchants(merchants.data);
      const merchantKeys = extractKey(processedMerchants, 'filterKey');
      const corpKeys = extractKey(processedMerchants, 'corp');
      const regionKeys = extractKey(processedMerchants, 'region');
      const principalKeys = extractKey(processedMerchants, 'principal');
      const associateKeys = extractKey(processedMerchants, 'associate');
      const chainKeys = extractKey(processedMerchants, 'chain');
      const uniqueCorpKeys = [...new Set(corpKeys)];
      const uniqueRegionKeys = [...new Set(regionKeys)];
      const uniquePrincipalKeys = [...new Set(principalKeys)];
      const uniqueAssociateKeys = [...new Set(associateKeys)];
      const uniqueChainKeys = [...new Set(chainKeys)];
      let mode = '';
      if (
        uniqueCorpKeys.length === 1 &&
        uniqueRegionKeys.length === 1 &&
        uniquePrincipalKeys.length === 1 &&
        uniqueAssociateKeys.length === 1 &&
        uniqueChainKeys.length === 1
      ) {
        mode = 'single-chain';
      } else if (
        uniqueCorpKeys.length === 1 &&
        uniqueRegionKeys.length === 1 &&
        uniquePrincipalKeys.length === 1 &&
        uniqueAssociateKeys.length === 1 &&
        uniqueChainKeys.length >= 2
      ) {
        mode = 'multi-chain';
      } else {
        mode = 'multi-corp';
      }
      const response = {
        ...userResponse.data,
        merchants: processedMerchants,
        uniqueMerchantKeys: [...new Set(merchantKeys)],
        uniqueCorpKeys,
        uniqueRegionKeys: [...new Set(regionKeys)],
        uniquePrincipalKeys: [...new Set(principalKeys)],
        uniqueAssociateKeys: [...new Set(associateKeys)],
        uniqueChainKeys: [...new Set(chainKeys)],
        mode,
      };
      return response;
    })
    .catch(onError);
}

function extractKey(array, key) {
  return array.map((item) => {
    return item[key];
  });
}

function processMerchants(merchants) {
  const processedMerchants = merchants.map((merchant) => {
    const filterKey = `${merchant.corp}.${merchant.region}.${merchant.principal}.${merchant.associate}.${merchant.chain}`;
    const filterKeys = {
      corp: { id: merchant.corpId, label: merchant.corp },
      region: { id: merchant.regionId, label: merchant.region },
      principal: { id: merchant.principalId, label: merchant.principal },
      associate: { id: merchant.associateId, label: merchant.associate },
      chain: { id: merchant.chainId, label: merchant.chain },
    };
    return {
      ...merchant,
      filterKey,
      filterKeys,
    };
  });
  return processedMerchants;
}

// eslint-disable-next-line no-unused-vars
function processDataAccess(dataAccess) {
  let daMerchants = [];
  const daKeys = [];
  dataAccess.forEach((lev0) => {
    const lev0Label = lev0.label;
    lev0.organizations.forEach((lev1) => {
      const lev1Label = lev1.label;
      lev1.organizations.forEach((lev2) => {
        const lev2Label = lev2.label;
        lev2.organizations.forEach((lev3) => {
          const lev3Label = lev3.label;
          lev3.organizations.forEach((lev4) => {
            const lev4Label = lev4.label;
            const merchantKey = `${lev0Label}.${lev1Label}.${lev2Label}.${lev3Label}.${lev4Label}`;
            const filterKey = {
              corp: { id: lev0.id, label: lev0.label },
              region: { id: lev1.id, label: lev1.label },
              principal: { id: lev2.id, label: lev2.label },
              associate: { id: lev3.id, label: lev3.label },
              chain: { id: lev4.id, label: lev4.label },
              merchantKey,
            };
            daKeys.push(filterKey);
            lev4.organizations.forEach((merchant) => {
              merchant.merchantKey = merchantKey;
              merchant.filterKey = filterKey;
              delete merchant.organizations;
            });
            daMerchants = [...daMerchants, ...lev4.organizations];
            delete lev4.organizations;
          });
          // console.log(lev3.organizations);
        });
        // console.log(lev2.organizations);
      });
      // console.log(lev1.organizations);
    });
    // console.log(lev0.organizations);
  });
  // console.log(JSON.stringify(result));
  const result = {};
  result.merchantAccess = daMerchants;
  result.dataAccess = dataAccess;
  result.filterKeys = daKeys;
  return result;
}

export function getUserOrganizationsTopLevel(userId, orgId, getShedule, hierarchyCode) {
  configureHttpHeaders(axios);
  let queryString = '';
  if (orgId && orgId.length > 0) {
    queryString = `?organizationId=${orgId}`;
  } else if (hierarchyCode) {
    queryString = `?hierarchyCode=${hierarchyCode}`;
  }
  let url = `${baseUrl}users/${userId}/organizations${queryString}`;
  if (getShedule) {
    url = `${baseUrl}users/${userId}/oganizations/details${queryString}`;
  }

  return axios.get(url).then((resp) => {
    const hierarchyLevels = ['corp', 'region', 'principal', 'associate', 'chain', 'merchant'];
    const mapOrganization = (o, orgLevelIndex, id, label, parentOrgLabel) => {
      return {
        id,
        hierarchyCode: (o.corp || '') + (o.region || '') + (o.principal || '') + (o.associate || '') + (o.chain || ''),
        label,
        parentOrgLabel,
        name: orgLevelIndex === 5 ? o.merchantName : '',
        hasAccess: id > 0,
        orgLevelIndex,
        organizations: [],
        type: o.type,
      };
    };
    const mapOrganizations = (arr, hierarchyTier) => {
      let pseudoIndex = -10000;
      const results = [];
      arr.forEach((o) => {
        const orgLevelIndex = hierarchyLevels.indexOf(o.type.toLowerCase());
        let mapped;
        const parentLabel = hierarchyTier > 0 ? o[hierarchyLevels[hierarchyTier - 1]] : null;
        if (orgLevelIndex === hierarchyTier) {
          const label = o[o.type.toLowerCase()];
          mapped = mapOrganization(o, orgLevelIndex, +o.organizationId, label, parentLabel);
        } else if (orgLevelIndex > hierarchyTier) {
          mapped = mapOrganization(o, hierarchyTier, pseudoIndex, o[hierarchyLevels[hierarchyTier]], parentLabel);
          pseudoIndex -= 1;
        }

        if (mapped) {
          const parentHierarchyLevels =
            mapped.orgLevelIndex > 0 ? hierarchyLevels.slice(0, mapped.orgLevelIndex - 6) : [];
          const parentHierarchyCode = parentHierarchyLevels.reduce((code, level) => code + o[level], '');
          mapped.parentHierarchyCode = parentHierarchyCode;
          mapped.hierarchyCode = parentHierarchyCode + mapped.label;
          if (
            !results.find(
              (x) =>
                x.label === mapped.label &&
                x.orgLevelIndex === mapped.orgLevelIndex &&
                x.parentLabel === mapped.parentLabel &&
                x.hierarchyCode === mapped.hierarchyCode,
            )
          ) {
            results.push(mapped);
          }
        }
      });
      return results;
    };

    const orgs = resp.data.content;
    const corps = mapOrganizations(orgs, 0);
    const regions = mapOrganizations(orgs, 1);
    const principals = mapOrganizations(orgs, 2);
    const associates = mapOrganizations(orgs, 3);
    const chains = mapOrganizations(orgs, 4);
    const merchants = mapOrganizations(orgs, 5);

    const appendChildrenToParents = (childList, parentList) => {
      childList.forEach((c) => {
        const parent = parentList.find((p) => p.hierarchyCode === c.parentHierarchyCode);
        if (parent) {
          parent.organizations.push(c);
        }
      });
    };

    appendChildrenToParents(merchants, chains);
    appendChildrenToParents(chains, associates);
    appendChildrenToParents(associates, principals);
    appendChildrenToParents(principals, regions);
    appendChildrenToParents(regions, corps);

    // visit each node and update it's hierarchy code
    const updateHierarchyCode = (arr, parentSuffix) => {
      arr.forEach((o) => {
        o.hierarchyCode = parentSuffix + o.label;
        if (o.organizations) updateHierarchyCode(o.organizations, o.hierarchyCode);
      });
    };
    updateHierarchyCode(corps, '');

    return corps;
  });
}

export function getUserOrganizationsForOrgId(userId, orgId, hierarchyCode) {
  configureHttpHeaders(axios);
  const hierarchyLevels = ['corp', 'region', 'principal', 'associate', 'chain', 'merchant'];

  let queryString = '';
  if (orgId && orgId > 0) {
    queryString = `?organizationId=${orgId}`;
  } else if (hierarchyCode) {
    queryString = `?hierarchyCode=${hierarchyCode}`;
  }
  const url = `${baseUrl}users/${userId}/organizations${queryString}`;

  return axios.get(url).then((r) => {
    const dataAccess = r.data.content.map((o) => {
      const orgHierarchyCode =
        (o.corp || '') + (o.region || '') + (o.principal || '') + (o.associate || '') + (o.chain || '');

      return {
        id: +o.organizationId,
        hierarchyCode: orgHierarchyCode,
        label: o[o.type.toLowerCase()],
        name: o.merchantName,
        hasAccess: true,
        orgLevelIndex: hierarchyLevels.indexOf(o.type.toLowerCase()),
        organizations: [],
      };
    });
    return dataAccess;
  });
}

export function getUserOrganizations(userId, orgId, hierarchyCode) {
  return getUserOrganizationsTopLevel(userId, orgId, hierarchyCode);
}

export function getBatchOrganizations(userId, parentCodes) {
  const promises = [];
  parentCodes.forEach((hierarchyCode) => {
    promises.push(getUserOrganizationsForOrgId(userId, null, hierarchyCode));
  });
  return Promise.all(promises).then((childOrgs) => {
    // const resultIds = [].concat(...childOrgs).map((o) => o.id);
    const results = parentCodes.map((parentCode, index) => {
      return { parentHierarchyCode: parentCode, childOrgs: childOrgs[index] };
    });
    return results;
  });
}

export function getUserOnBoarding() {
  configureHttpHeaders(axios);
  return get('users/onBoarding');
}

export function updateUserOnBoardingStatus(topicKey) {
  configureHttpHeaders(axios);
  return put(`users/onBoarding?topicKey=${topicKey}`, null);
}

export function getPrimaryMerchantByUserId(userId) {
  configureHttpHeaders(axios);

  return get(`users/${userId}/primary-merchant`);
}

export function getUserDataAccessById(userId) {
  configureHttpHeaders(axios);

  return get(`users/${userId}/dataaccess`);
}

export function getDataAccessRoleWise() {
  configureHttpHeaders(axios);

  return get('users/validDataAccess');
}

export function getHierarchyBasedReport(userId) {
  configureHttpHeaders(axios);

  return get(`users/${userId}/hierarchyReport`);
}

export function getUserMerchants(userId) {
  configureHttpHeaders(axios);

  return get(`users/${userId}/merchants`);
}

export function saveUser(user) {
  configureHttpHeaders(axios);
  const userWithFilteredRole = filterUserRoleForSaving(user);

  if (user.userId) {
    const filteredExistingUser = filterExistingUserObj(userWithFilteredRole);

    return put(`users/${user.userId}`, filteredExistingUser);

    // No userId, so Create a New User
  } else {
    // eslint-disable-line no-else-return
    const filteredNewUser = filterNewUserObj(userWithFilteredRole);

    return post('users', filteredNewUser);
  }
}

export function updateUserStatusToggle(user) {
  configureHttpHeaders(axios);
  return put(`users/${user.userId}/${user.statusKey}`, user);
}

export function saveUserDataAccess(user) {
  configureHttpHeaders(axios);
  const userWithFilteredRole = filterUserRoleForSaving(user);
  const filteredExistingUser = filterExistingUserObjDataAccess(userWithFilteredRole);
  return put(`users/${user.userId}/dataaccess`, filteredExistingUser);
}

export function setUserTermsAccepted(termsId) {
  configureHttpHeaders(axios);

  return post(`users/terms/${termsId}`);
}

export function getUserTerms(webToken) {
  axios.defaults.headers.common['Authorization'] = webToken; // eslint-disable-line no-param-reassign, dot-notation
  return get('users/terms');
}

export function fetchDocumentAccess() {
  configureHttpHeaders(axios);
  return get('users/documentaccess');
}

export function apiGetAcquirerAdmin(roleId, acquirerId) {
  configureHttpHeaders(axios);
  return get(`users/parents/${roleId}/${acquirerId}`);
}

export function apiSaveUserProducts(userProductsToSave, userId) {
  configureHttpHeaders(axios);
  const productsToUpdate = filterExistingUserObjForProducts(userProductsToSave);
  return put(`products/save/${userId}`, productsToUpdate);
}

export function apiGetUserProducts(userId) {
  configureHttpHeaders(axios);

  return get(`products/user/${userId}`);
}

export function apiProductsbyAcquirerId(acquirerId, languageAbbr) {
  configureHttpHeaders(axios);

  return get(`products?acquirerId=${acquirerId}&languageAbbr=${languageAbbr}`);
}

export function apiProductsByPrimaryMerchant(merchantNum, languageAbbr) {
  configureHttpHeaders(axios);

  return get(`products?languageAbbr=${languageAbbr}&primaryMerchant=${merchantNum}`);
}
// eslint-disable-next-line no-shadow
async function get(url, signal = null) {
  /* eslint-enable dot-notation */
  try {
    const response = await axios.get(`${baseUrl}${url}`, {
      signal,
    });
    return onSuccess(response);
  } catch (error) {
    return onError(error);
  }
}

// dda encrypt post
async function postSelfReg(url, secureMerchantData) {
  try {
    const response = await axios.post(`${baseUrl}${url}`, secureMerchantData);
    return onSelfRegSuccess(response);
  } catch (error) {
    return onError(error);
  }
}

async function put(url, user) {
  try {
    const response = await axios.put(`${baseUrl}${url}`, user);
    return onSuccess(response);
  } catch (error) {
    return onError(error);
  }
}

function post(url, user) {
  return axios.post(`${baseUrl}${url}`, user).then(onSuccess).catch(onError);
}

function onSuccess(response) {
  let result;

  if (response.status !== 204) {
    result = response.data;
  } else {
    result = 'Success';
  }
  return result;
}

export function onSelfRegSuccess(response) {
  let result;
  if (response.status !== 204) {
    result = response.data ? response : (response = { data: 'Success', status: 200 });
  } else {
    result = 'Success';
  }
  return result;
}

function onError(error) {
  const returnedError = error && error.response && error.response.data;
  throw returnedError;
}

export function savePreferences(prefs, userId) {
  configureHttpHeaders(axios);

  const allowedKeys = ['dateFormat', 'firstName', 'language', 'lastName', 'timeFormat'];
  const filteredPrefs = filterRequestPayloadObject(prefs, allowedKeys);

  return put(`profile/preferences/${userId}`, filteredPrefs);
}

function filterUserRoleForSaving(user) {
  const allowedRoleKeys = ['id', 'permissionIds'];
  const filteredRole = filterRequestPayloadObject(user.role, allowedRoleKeys);
  const userWithFilteredRole = Object.assign({}, user, { role: filteredRole });

  return userWithFilteredRole;
}

function filterNewUserObj(user) {
  const allowedNewUserKeys = [
    'firstName',
    'lastName',
    'email',
    'role',
    'acquirerId',
    'organizationIds',
    'userId',
    'primaryMerchant',
    'parentUserId',
  ];
  const filteredNewUser = filterRequestPayloadObject(user, allowedNewUserKeys);

  return filteredNewUser;
}

function filterExistingUserObjForProducts(productsToSave) {
  const allowedKeysForProducts = ['products'];
  const filteredUpdatedProducts = filterRequestPayloadObject(productsToSave, allowedKeysForProducts);
  return filteredUpdatedProducts;
}
function filterExistingUserObj(user) {
  const userWithFilteredPreferences = filterUserPreferencesForSaving(user);

  const allowedExistingUserKeys = [
    'firstName',
    'lastName',
    'email',
    'role',
    'acquirerId',
    'organizationIds',
    'preferences',
    'status',
  ];
  const filteredExistingUser = filterRequestPayloadObject(userWithFilteredPreferences, allowedExistingUserKeys);

  return filteredExistingUser;
}

function filterExistingUserObjDataAccess(user) {
  const userWithFilteredPreferences = filterUserPreferencesForSaving(user);

  const allowedExistingUserKeys = ['organizationIds'];
  const filteredExistingUser = filterRequestPayloadObject(userWithFilteredPreferences, allowedExistingUserKeys);

  return filteredExistingUser;
}

function filterUserPreferencesForSaving(user) {
  const allowedPreferencesKeys = ['dateFormat', 'firstName', 'lastName', 'language', 'timeFormat'];
  const filteredPreferences = filterRequestPayloadObject(user.preferences, allowedPreferencesKeys);
  const userWithFilteredPreferences = Object.assign({}, user, { preferences: filteredPreferences });

  return userWithFilteredPreferences;
}

export function getRegisteredMerchant(merchantData, secureMerchantData) {
  axios.defaults.headers.common['Authorization'] = merchantData.sessionToken; // eslint-disable-line dot-notation
  axios.defaults.headers.common['userLang'] = merchantData.language; // eslint-disable-line dot-notation
  return postSelfReg('self-reg/merchant/', secureMerchantData);
}

export function apiGetSelfRegisterProducts(userId) {
  configureHttpHeaders(axios);
  return get(`products/self/${userId}`);
}

export function apiRegisterMerchant(merchantData) {
  configureHttpHeaders(axios);
  const body = {
    ddaNumber: merchantData.ddaTaxId,
    merchantNumber: merchantData.merchantNumber,
    accountAdminMID: merchantData.accountAdminMID,
  };
  return post('users/newMID', body);
}

export default {
  getUsers,
  getUserCount,
  // getUserById,
  getUserDataAccessById,
  getDataAccessRoleWise,
  getPrimaryMerchantByUserId,
  saveUser,
  saveUserDataAccess,
  updateUserStatusToggle,
  fetchDocumentAccess,
  apiSaveUserProducts,
  savePreferences,
  getRegisteredMerchant,
  apiGetUserProducts,
  apiGetSelfRegisterProducts,
  setUserTermsAccepted,
  getUserOrganizationsTopLevel,
  getUserOrganizationsForOrgId,
  getUserOrganizations,
  getBatchOrganizations,
  apiRegisterMerchant,
};
